import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import MessageBubble from "@/components/MessageBubble";
import {
  Hash, Plus, Send, ChevronDown, Copy, LogOut, MessageSquare, Users, Loader2,
} from "lucide-react";

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function Workspace() {
  const { user, logout } = useAuth();
  const { t, roleLabel } = useLang();
  const [tenant, setTenant] = useState(null);
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [newChannel, setNewChannel] = useState("");
  const [channelOpen, setChannelOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const scrollRef = useRef(null);

  const loadConvos = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setChannels(data.channels);
    setDms(data.dms);
    return data;
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/tenants/me").then((r) => setTenant(r.data)),
      api.get("/members").then((r) => setMembers(r.data)),
      loadConvos(),
    ]).then(([, , data]) => {
      if (data.channels.length && !active) setActive(data.channels[0]);
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [loadConvos]); // eslint-disable-line

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const { data } = await api.get(`/conversations/${convId}/messages`);
      setMessages(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!active) return;
    loadMessages(active.conversation_id);
    const timer = setInterval(() => loadMessages(active.conversation_id), 4000);
    return () => clearInterval(timer);
  }, [active, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = text.trim();
    if (!body || !active) return;
    setSending(true);
    setText("");
    try {
      const { data } = await api.post(`/conversations/${active.conversation_id}/messages`, { text: body });
      setMessages((m) => [...m, data]);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setText(body);
    } finally {
      setSending(false);
    }
  };

  const createChannel = async () => {
    if (!newChannel.trim()) return;
    try {
      const { data } = await api.post("/conversations/channel", { name: newChannel });
      setNewChannel("");
      setChannelOpen(false);
      await loadConvos();
      setActive(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startDM = async (m) => {
    try {
      const { data } = await api.post("/conversations/dm", { user_id: m.user_id });
      setMembersOpen(false);
      await loadConvos();
      setActive(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const copyInvite = () => {
    if (tenant?.invite_code) {
      navigator.clipboard.writeText(tenant.invite_code);
      toast.success(t("ws.inviteCopied"));
    }
  };

  return (
    <div className="relative z-10 h-screen flex bg-background overflow-hidden">
      {/* Sidebar */}
      <aside className="w-72 border-r border-border flex flex-col bg-card shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center justify-between w-full px-4 h-16 border-b border-border hover:bg-muted/50 transition-colors" data-testid="tenant-menu-trigger">
              <div className="text-left">
                <div className="font-heading font-bold tracking-tight truncate max-w-[180px]">{tenant?.name || "..."}</div>
                <div className="text-xs text-muted-foreground">{roleLabel(user.role)}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>{t("ws.workspace")}</DropdownMenuLabel>
            <DropdownMenuItem onClick={copyInvite} data-testid="copy-invite-item">
              <Copy className="w-4 h-4 mr-2" /> {t("ws.inviteCode")} <span className="font-mono font-semibold ml-1">{tenant?.invite_code}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-item">
              <LogOut className="w-4 h-4 mr-2" /> {t("ws.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex-1 overflow-y-auto scroll-slim px-2 py-4">
          {/* Channels */}
          <div className="flex items-center justify-between px-2 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("ws.channels")}</span>
            <Dialog open={channelOpen} onOpenChange={setChannelOpen}>
              <DialogTrigger asChild>
                <button className="text-muted-foreground hover:text-primary" data-testid="new-channel-btn"><Plus className="w-4 h-4" /></button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("ws.newChannel")}</DialogTitle></DialogHeader>
                <Label htmlFor="ch">{t("ws.channelName")}</Label>
                <Input id="ch" value={newChannel} onChange={(e) => setNewChannel(e.target.value)} placeholder={t("ws.channelPh")} data-testid="channel-name-input" />
                <DialogFooter>
                  <Button onClick={createChannel} data-testid="create-channel-submit">{t("ws.create")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {channels.map((c) => (
            <button key={c.conversation_id} onClick={() => setActive(c)} className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm transition-colors ${active?.conversation_id === c.conversation_id ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted text-foreground"}`} data-testid={`channel-${c.name}`}>
              <Hash className="w-4 h-4 shrink-0" /> <span className="truncate">{c.name}</span>
            </button>
          ))}

          {/* DMs */}
          <div className="flex items-center justify-between px-2 mb-1 mt-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("ws.dms")}</span>
            <Dialog open={membersOpen} onOpenChange={setMembersOpen}>
              <DialogTrigger asChild>
                <button className="text-muted-foreground hover:text-primary" data-testid="new-dm-btn"><Plus className="w-4 h-4" /></button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("ws.teamMembers")}</DialogTitle></DialogHeader>
                <div className="max-h-80 overflow-y-auto scroll-slim -mx-2 px-2">
                  {members.filter((m) => m.user_id !== user.user_id).map((m) => (
                    <button key={m.user_id} onClick={() => startDM(m)} className="flex items-center gap-3 w-full px-2 py-2 rounded-md hover:bg-muted transition-colors text-left" data-testid={`member-${m.user_id}`}>
                      <div className="w-9 h-9 rounded-md bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{initials(m.name)}</div>
                      <div>
                        <div className="text-sm font-medium">{m.name}</div>
                        <div className="text-xs text-muted-foreground">{roleLabel(m.role) || t("ws.noRole")}</div>
                      </div>
                    </button>
                  ))}
                  {members.filter((m) => m.user_id !== user.user_id).length === 0 && (
                    <p className="text-sm text-muted-foreground p-2">{t("ws.noOtherMembers")}</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {dms.map((c) => (
            <button key={c.conversation_id} onClick={() => setActive(c)} className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm transition-colors ${active?.conversation_id === c.conversation_id ? "bg-primary/10 text-primary font-semibold" : "hover:bg-muted"}`} data-testid={`dm-${c.conversation_id}`}>
              <div className="w-5 h-5 rounded bg-primary/10 text-primary flex items-center justify-center text-[9px] font-bold shrink-0">{initials(c.name)}</div>
              <span className="truncate">{c.name}</span>
            </button>
          ))}
        </div>

        {/* current user */}
        <div className="border-t border-border p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">{initials(user.name)}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user.email}</div>
          </div>
          <LanguageSwitcher />
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border flex items-center px-6 gap-2 bg-card shrink-0">
          {active ? (
            <>
              {active.type === "channel" ? <Hash className="w-5 h-5 text-muted-foreground" /> : <MessageSquare className="w-5 h-5 text-muted-foreground" />}
              <span className="font-heading font-bold tracking-tight text-lg">{active.name}</span>
              {active.type === "dm" && active.peer && (
                <span className="text-xs uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5 ml-1">{roleLabel(active.peer.role)}</span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">{t("ws.selectChat")}</span>
          )}
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-slim px-6 py-6 space-y-5" role="log" data-testid="message-log">
          {active && messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4"><Users className="w-6 h-6" /></div>
              <p className="font-medium">{t("ws.noMessages")}</p>
              <p className="text-sm">{t("ws.noMessagesSub")}</p>
            </div>
          )}
          {messages.map((m) => <MessageBubble key={m.message_id} msg={m} />)}
        </div>

        {active && (
          <div className="border-t border-border p-4 bg-card shrink-0">
            <div className="flex items-end gap-3 border border-border rounded-lg focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition-shadow p-2">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={`${t("ws.composerA")}${active.name}${t("ws.composerB")}`}
                className="border-0 focus-visible:ring-0 resize-none min-h-[44px] max-h-40"
                data-testid="message-input"
              />
              <Button onClick={send} disabled={sending || !text.trim()} size="icon" className="rounded-lg h-10 w-10 shrink-0" data-testid="send-message-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
